import { AlertComponent } from 'Components/HelperComponents/AlertComponent'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import Button from 'NewVersion/components/UI/Buttons/Button'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import 'NewVersion/styles/EntitySelector.scss'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'
import { copyObjecIds, getCurrentJWToken, getEmailFromToken } from 'utils/utils'

/**
 * Extracts a clean entity path from various formats
 * Handles: full URLs, @id fields, path fields, or raw paths
 * Returns format: /year/entity_id
 */
const extractEntityPath = (entity) => {
  const rawPath = entity?.path || entity?.['@id'] || entity
  if (!rawPath) return null

  // If it's a full URL (contains ://), extract the last 2 segments
  if (typeof rawPath === 'string' && rawPath.includes('://')) {
    return '/' + rawPath.split('/').slice(-2).join('/')
  }

  // Ensure path starts with /
  if (typeof rawPath === 'string') {
    return rawPath.startsWith('/') ? rawPath : `/${rawPath}`
  }

  return null
}

/**
 * EntitySelector - Component for selecting or creating an entity (artist or representant)
 *
 * @param {string} role - 'artista' or 'representant'
 * @param {function} onEntitySelected - Callback when an entity is selected
 * @param {function} onBack - Callback to go back
 * @param {object} representant - If selecting artist for a representant, the representant object
 * @param {boolean} isArtistForRepresentant - True if selecting artist for a representant
 */
export const EntitySelector = ({
  role,
  onEntitySelected,
  onBack,
  representant = null,
  isArtistForRepresentant = false,
}) => {
  const { t } = useTranslation()
  const { edition, loading: editionLoading } = useEdition()
  const [copying, setCopying] = useState(false)
  const [error, setError] = useState(null)

  const entityType =
    role === ENS_TYPES_OBJ.artista.text || isArtistForRepresentant
      ? ENS_TYPES_OBJ.artista.text
      : ENS_TYPES_OBJ.representant.text
  const ensTypeObj = ENS_TYPES_OBJ[entityType]

  // Query to get entities
  const {
    data: entitiesResult,
    isLoading: entitiesLoading,
    error: entitiesError,
  } = useGuillotina({
    path: `@search?type_name=Ens&tipus=${entityType}&owners=${getEmailFromToken()}&_sort_des=creation_date`,
  })

  // Filter and group entities based on context
  const { currentEditionEntities, pastEditionEntities } = useMemo(() => {
    if (!entitiesResult?.items) return { currentEditionEntities: [], pastEditionEntities: [] }

    let filtered = entitiesResult.items

    // If selecting artist for a representant, only show artists that belong to this representant
    if (isArtistForRepresentant && representant) {
      const repPathForFilter = extractEntityPath(representant)
      const repTitle = representant.title

      filtered = filtered.filter((artist) => {
        const artistRepPath = artist.representant
        if (!artistRepPath) return false

        const isFromPastEdition = !artist.path?.startsWith(`/${edition}/`)
        if (isFromPastEdition) {
          return artist.title_representant === repTitle
        }

        return artistRepPath === repPathForFilter
      })
    }

    // Group by edition
    const current = filtered.filter((e) => e.path?.startsWith(`/${edition}/`))
    const past = filtered.filter((e) => !e.path?.startsWith(`/${edition}/`))

    return { currentEditionEntities: current, pastEditionEntities: past }
  }, [entitiesResult, isArtistForRepresentant, representant, edition])

  const hasEntities = currentEditionEntities.length > 0 || pastEditionEntities.length > 0

  // Handle entity selection
  const handleSelect = async (entity) => {
    const entityYear = entity.year || entity.path?.split('/')[1]

    // If entity is from a previous edition, copy it
    if (entityYear !== edition) {
      await copyEntityToCurrentEdition(entity)
    } else {
      // Fetch full entity data
      const token = getCurrentJWToken()
      try {
        const fullEntity = await fetchGuillotina({
          path: entity.path,
          method: 'GET',
          token,
        })
        // Preserve title_representant from search result (not returned by GET)
        if (entity.title_representant && !fullEntity.title_representant) {
          fullEntity.title_representant = entity.title_representant
        }
        onEntitySelected(fullEntity)
      } catch (err) {
        setError(t('error_loading_entity'))
      }
    }
  }

  // Copy entity from previous edition to current
  const copyEntityToCurrentEdition = async (entity) => {
    setCopying(true)
    setError(null)
    const token = getCurrentJWToken()

    try {
      // Get full entity data
      const response = await fetchGuillotina({
        path: entity.path,
        method: 'GET',
        token,
      })

      // Prepare data for copy - exclude 'representant' as we'll set it manually
      const idsCopy = Object.values(ensTypeObj.orderFieldsForm)
        .reduce((previousValue, currentValue) => [...previousValue, ...currentValue.fields], [])
        .filter((field) => field !== 'representant') // Don't copy old representant

      const newEntity = copyObjecIds(response, ['@type', 'tipus', 'tarifa', ...idsCopy])

      // Remove any old representant that might have been copied
      delete newEntity.representant

      // If copying an artist for a representant, set the representant
      if (isArtistForRepresentant && representant) {
        newEntity.representant = extractEntityPath(representant)
      }

      // Create new entity in current edition
      const created = await fetchGuillotina({
        path: edition,
        method: 'POST',
        data: newEntity,
        token,
      })

      // Get the path from the response
      const newPath = extractEntityPath(created)

      // Fetch the created entity with the clean path
      const fullEntity = await fetchGuillotina({
        path: newPath,
        method: 'GET',
        token,
      })

      onEntitySelected(fullEntity)
    } catch (err) {
      // If entity already exists, try to find and select it
      if (err.info?.code === 'already-exists') {
        try {
          // Search for the entity by title in current edition (use entity.title from the original)
          const searchResult = await fetchGuillotina({
            path: `@search?type_name=Ens&tipus=${entityType}&title__eq=${encodeURIComponent(
              entity.title
            )}&year=${edition}`,
            method: 'GET',
            token,
          })

          if (searchResult.items && searchResult.items.length > 0) {
            // Found existing entity, fetch full data and select it
            const existingEntity = await fetchGuillotina({
              path: searchResult.items[0].path,
              method: 'GET',
              token,
            })
            onEntitySelected(existingEntity)
            return
          }
        } catch (searchErr) {
          console.error('Error searching for existing entity:', searchErr)
        }
        setError(t('entity_already_exists'))
      } else {
        setError(t('error_copying_entity') + (err.message ? `: ${err.message}` : ''))
      }
    } finally {
      setCopying(false)
    }
  }

  // Handle create new entity
  const handleCreateNew = () => {
    // Navigate to entity creation - will be handled by parent
    // For now, we pass a special object indicating creation
    onEntitySelected({ __createNew: true, type: entityType })
  }

  if (editionLoading || entitiesLoading || copying) {
    return <LoadingComponent />
  }

  if (entitiesError) {
    return <AlertComponent type="danger">{t('error_loading_entities')}</AlertComponent>
  }

  const getTitle = () => {
    if (isArtistForRepresentant) {
      return t('select_artist_for_proposal')
    }
    return role === ENS_TYPES_OBJ.artista.text
      ? t('select_your_artist_profile')
      : t('select_your_representant_profile')
  }

  return (
    <div className="entity-selector">
      <GoBackButton onClick={onBack} text={t('tornar')} />

      <p className="entity-selector__title">{getTitle()}</p>

      {error && <AlertComponent type="danger">{error}</AlertComponent>}

      {hasEntities ? (
        <>
          {/* Current edition entities */}
          {currentEditionEntities.length > 0 && (
            <>
              <p className="entity-selector__section-label">
                {t('edicio_actual')} ({edition})
              </p>
              <div className="entity-selector__list">
                {currentEditionEntities.map((entity) => (
                  <EntitySelectorItem
                    key={entity.path || entity['@id']}
                    entity={entity}
                    currentEdition={edition}
                    onSelect={() => handleSelect(entity)}
                    showRepresentant={
                      role === ENS_TYPES_OBJ.artista.text && !isArtistForRepresentant
                    }
                  />
                ))}
              </div>
            </>
          )}

          {/* Past edition entities */}
          {pastEditionEntities.length > 0 && (
            <>
              <p className="entity-selector__section-label">{t('edicions_anteriors')}</p>
              <div className="entity-selector__list">
                {pastEditionEntities.map((entity) => (
                  <EntitySelectorItem
                    key={entity.path || entity['@id']}
                    entity={entity}
                    currentEdition={edition}
                    onSelect={() => handleSelect(entity)}
                    showRepresentant={
                      role === ENS_TYPES_OBJ.artista.text && !isArtistForRepresentant
                    }
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <p className="entity-selector__empty">
          {role === ENS_TYPES_OBJ.artista.text
            ? t('no_artists_found')
            : t('no_representants_found')}
        </p>
      )}

      <div className="entity-selector__separator"></div>

      <Button
        type="button"
        color="primaryOutlinedIcon outlined"
        onClick={handleCreateNew}
        dataTest={`btnCreateNew${entityType}`}
      >
        +{' '}
        {role === ENS_TYPES_OBJ.artista.text || isArtistForRepresentant
          ? t('create_new_artist_profile')
          : t('create_new_representant_profile')}
      </Button>
    </div>
  )
}

/**
 * EntitySelectorItem - Individual item in the entity list
 */
const EntitySelectorItem = ({ entity, currentEdition, onSelect, showRepresentant }) => {
  const { t } = useTranslation()
  const entityYear = entity.year || entity.path?.split('/')[1]
  const isFromPastEdition = entityYear !== currentEdition

  return (
    <div className="entity-selector-item">
      <div className="entity-selector-item__avatar">{entity.title?.[0]?.toUpperCase() || '?'}</div>

      <div className="entity-selector-item__content">
        <p className="entity-selector-item__title">
          {entity.title} ({entityYear})
        </p>
        {showRepresentant && entity.representant && (
          <p className="entity-selector-item__representant">
            {t('representant')}: {entity.title_representant || entity.representant.split('/').pop()}
          </p>
        )}
        {showRepresentant && !entity.representant && (
          <p className="entity-selector-item__no-representant">{t('sense_representant')}</p>
        )}
        {isFromPastEdition && (
          <p className="entity-selector-item__warning">{t('will_be_copied_to_current_edition')}</p>
        )}
      </div>

      <Button
        type="button"
        color={isFromPastEdition ? 'secondary' : 'primary'}
        onClick={onSelect}
        dataTest={`btnSelectEntity_${entity.path?.split('/').pop()}`}
      >
        {isFromPastEdition ? t('use_for_current_edition') : t('seleccionar')}
      </Button>
    </div>
  )
}

export default EntitySelector
